<template>
  <Teleport to="body">
    <Transition name="modal-fade">
      <div v-if="visible" class="share-modal-overlay" @click.self="close">
      <div class="share-modal">
        <div class="modal-header">
          <h3>分享内容</h3>
          <button class="close-btn" @click="close">
            <i class="fa-solid fa-times"></i>
          </button>
        </div>
        
        <div class="modal-body">
          <!-- 复制链接区域 -->
          <div class="copy-section">
            <div class="share-text-box">{{ shareText }}</div>
            <button class="copy-btn" @click="copyToClipboard">
              <i class="fa-solid fa-copy"></i>
              {{ copyBtnText }}
            </button>
          </div>
          
          <div class="divider">
            <span>或者分享给好友</span>
          </div>
          
          <!-- 好友列表区域 -->
          <div class="friends-section">
            <div v-if="loadingFriends" class="loading">
              <i class="fa-solid fa-spinner fa-spin"></i> 加载好友列表中...
            </div>
            
            <div v-else-if="!currentUser" class="empty-state">
              <i class="fa-solid fa-user-lock empty-icon"></i>
              <p>登录后即可分享给好友</p>
              <button class="login-link-btn" @click="goToLogin">立即登录</button>
            </div>
            
            <div v-else-if="!friends || friends.length === 0" class="empty-state">
              <i class="fa-regular fa-face-sad-tear empty-icon"></i>
              <p>暂无互关好友可分享</p>
            </div>
            
            <div v-else class="friends-list">
               <div 
                 v-for="friend in friends" 
                 :key="friend.userId" 
                 class="friend-item"
                 :class="{ selected: selectedFriendId === friend.userId }"
                 @click="selectFriend(friend)"
               >
                 <div class="friend-avatar">
                   <img 
                     v-if="shouldShowAvatar(friend)"
                     :src="getAvatarSrc(friend)" 
                     alt="avatar"
                     style="width: 100%; height: 100%; object-fit: cover; border-radius: 50%;"
                     @error="handleAvatarError(friend)"
                   />
                   <span v-else>{{ getAvatarPlaceholder(friend) }}</span>
                 </div>
                 <div class="friend-info">
                   <span class="friend-name">{{ getDisplayName(friend) }}</span>
                   <span class="friend-status" :class="{ online: friend.isOnline }">
                     {{ friend.isOnline ? '在线' : '离线' }}
                   </span>
                 </div>
                 <div class="select-indicator">
                   <i class="fa-solid fa-check-circle" v-if="selectedFriendId === friend.userId"></i>
                   <i class="fa-regular fa-circle" v-else></i>
                 </div>
               </div>
            </div>
          </div>
        </div>
        
        <div class="modal-footer">
          <button class="cancel-btn" @click="close">取消</button>
          <button 
            class="confirm-share-btn" 
            :disabled="!selectedFriendId || isSending"
            @click="shareToFriend"
          >
            <i v-if="isSending" class="fa-solid fa-spinner fa-spin"></i>
            {{ isSending ? '发送中...' : '发送给好友' }}
          </button>
        </div>
      </div>
    </div>
  </Transition>
  </Teleport>
</template>

<script>
import followService from '@/services/followService'
import messagesService from '@/services/messagesService'
import authService from '@/services/authService'
import serverUrl, { avatarUrl } from '@/assets/BaseServerUrl.js'
import { message } from '@/utils/ui-service'
import eventBus from '@/utils/eventBus'
import axios from 'axios'

const AVATAR_EXTENSIONS = ['webp', 'png', 'jpg', 'jpeg', 'gif']

export default {
  name: 'ShareModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    shareText: {
      type: String,
      required: true
    }
  },
  emits: ['update:visible', 'close'],
  data() {
    return {
      friends: [],
      loadingFriends: false,
      selectedFriendId: null,
      isSending: false,
      copyBtnText: '复制链接',
      currentUser: null,
      userPrefs: {}, // 暂存好友备注等信息
      friendAvatarState: {} // { [userId]: { extIndex: 0, error: false } }
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.resetState()
        this.loadFriends()
      }
    }
  },
  methods: {
    resetState() {
      this.selectedFriendId = null
      this.isSending = false
      this.copyBtnText = '复制链接'
      this.currentUser = authService.getCurrentUser()
      this.friendAvatarState = {}
    },
    
    close() {
      this.$emit('update:visible', false)
      this.$emit('close')
    },

    goToLogin() {
      this.close()
      // 触发全局登录模态框
      eventBus.emit('show-login-modal')
    },
    
    copyToClipboard() {
      navigator.clipboard.writeText(this.shareText)
        .then(() => {
          this.copyBtnText = '已复制！'
          message.success('内容已复制到剪贴板')
          setTimeout(() => {
            if (this.visible) this.copyBtnText = '复制链接'
          }, 2000)
        })
        .catch(() => {
          message.error('复制失败，请手动复制')
        })
    },
    
    async loadFriends() {
      if (!this.currentUser || !this.currentUser.userId) return
      
      this.loadingFriends = true
      try {
        const res = await followService.getList(this.currentUser.userId)
        if (res.success) {
          // 只筛选互关好友（friends通常定义为互关）
          // 这里逻辑复用Chat.vue的逻辑：互关才算真正的好友，或者稍微放宽到关注列表
          // 为了确保能发消息，通常互关是前提，或者未开启陌生人限制
          // 简化逻辑：显示互关好友
          const followers = res.data.followers || []
          const followingDetails = res.data.followingDetails || []
          
          // 筛选出互关的好友
          this.friends = followingDetails.filter(u => followers.includes(u.userId))
          
          // 预获取好友头像
          await this.fetchAvatarsForFriends()
        }
      } catch (error) {
        console.error('加载好友列表失败:', error)
        message.error('加载好友列表失败')
      } finally {
        this.loadingFriends = false
      }
    },
    
    selectFriend(friend) {
      this.selectedFriendId = friend.userId
    },
    
    async shareToFriend() {
      if (!this.selectedFriendId || !this.currentUser) return
      
      this.isSending = true
      try {
        const res = await messagesService.sendMessage(
          this.currentUser.userId, 
          this.selectedFriendId, 
          this.shareText
        )
        
        if (res.success) {
          message.success('已分享给好友')
          this.close()
        } else {
          message.error(res.message || '分享失败')
        }
      } catch (error) {
        console.error('分享失败:', error)
        message.error('分享失败，请重试')
      } finally {
        this.isSending = false
      }
    },
    
    getDisplayName(friend) {
      return friend.remark || friend.username || friend.userId
    },
    
    getAvatarPlaceholder(friend) {
      const name = this.getDisplayName(friend)
      return name ? name.charAt(0).toUpperCase() : 'U'
    },
    
    // 初始化或获取好友头像状态
    getFriendState(friend) {
      if (!this.friendAvatarState[friend.userId]) {
        this.friendAvatarState[friend.userId] = { extIndex: 0, error: false, timestamp: Date.now() }
      }
      return this.friendAvatarState[friend.userId]
    },

    shouldShowAvatar(friend) {
        // 如果后端有avatar字段且非空，也可以优先使用（看需求，用户要求拼接尝试机制，所以我们主要走拼接逻辑）
        // 这里完全使用拼接逻辑作为主导
        const state = this.getFriendState(friend)
        return !state.error
    },

    getAvatarSrc(friend) {
      // 优先使用预获取的头像 URL
      if (friend.fetchedAvatarUrl) {
        return friend.fetchedAvatarUrl
      }
      // 回退到本地拼接逻辑
      const state = this.getFriendState(friend)
      const ext = AVATAR_EXTENSIONS[state.extIndex] || 'webp'
      const name = friend.username || friend.userId
      return `${avatarUrl}/avatar/${encodeURIComponent(name)}.${ext}`
    },
    
    handleAvatarError(friend) {
      const state = this.getFriendState(friend)
      state.extIndex++
      
      // 如果所有后缀都尝试过了
      if (state.extIndex >= AVATAR_EXTENSIONS.length) {
        state.error = true
      } else {
        // 强制刷新：由于 Vue 可能不会检测到深层对象属性变化触发 img src 重新加载
        // 通过更新 timestamp 或 key 来强制刷新组件是比较重的方式
        // 这里修改 extIndex 会改变 computed src 吗？
        // 我们的 getAvatarSrc 是 method。img :src 会响应式更新前提是依赖项变化。
        // friendAvatarState 是 data，且我们修改了其中的属性。
        // Vue 3 Proxy 应该能检测到。
        // 为了确保，我们赋新对象
        this.friendAvatarState[friend.userId] = { ...state }
      }
    },
    // 预获取好友头像
    async fetchAvatarsForFriends() {
      if (!this.friends || this.friends.length === 0) return;
      const promises = this.friends.map(async (friend) => {
        const username = friend.username;
        if (username) {
          const avatarUrl = await this.getUserAvatarUrl(username);
          if (avatarUrl) {
            friend.fetchedAvatarUrl = avatarUrl;
          }
        }
      });
      await Promise.all(promises);
    },
    // 调用后端接口获取用户头像 URL
    async getUserAvatarUrl(username) {
      if (!username) return null;
      try {
        const env = process.env.NODE_ENV === 'development' ? 'development' : 'production';
        const resp = await axios.get(`${serverUrl}/api/get-avatar-url`, { params: { username, env } })
        if (resp.data && resp.data.success && resp.data.url) {
          return resp.data.url
        }
      } catch (e) {
        // 静默失败
      }
      return null
    }
  }
}
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.share-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: var(--z-modal-elevated);

}

.share-modal {
  background: #fff;
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  max-height: 80vh;
}

.modal-header {
  padding: 15px 20px;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h3 {
  margin: 0;
  font-family: '猫啃网糖圆体';
  font-size: 1.2em;
  color: #333;
}

.close-btn {
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: #999;
  transition: color 0.3s;
}

.close-btn:hover {
  color: #333;
}

.modal-body {
  padding: 20px;
  flex: 1;
  overflow-y: auto;
}

.copy-section {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
}

.share-text-box {
  flex: 1;
  padding: 10px;
  background: #f5f5f5;
  border-radius: 8px;
  font-size: 13px;
  color: #555;
  word-break: break-all;
  max-height: 60px;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
}

.copy-btn {
  padding: 0 15px;
  background: #f0f0f0;
  border: 1px solid #dcdcdc;
  border-radius: 8px;
  cursor: pointer;
  font-family: '爱点风雅黑';
  color: #333;
  transition: all 0.3s;
  white-space: nowrap;
}

.copy-btn:hover {
  background: #e0e0e0;
}

.divider {
  display: flex;
  align-items: center;
  text-align: center;
  margin: 20px 0;
  color: #999;
  font-size: 12px;
}

.divider::before,
.divider::after {
  content: '';
  flex: 1;
  border-bottom: 1px solid #eee;
}

.divider span {
  padding: 0 10px;
  font-family: '爱点风雅黑';
}

.friends-section {
  min-height: 200px;
}

.friends-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.loading {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #999;
  font-size: 14px;
  gap: 8px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #999;
  text-align: center;
  padding: 20px 0;
}

.empty-icon {
  font-size: 32px;
  margin-bottom: 10px;
  color: #ddd;
}

.empty-state p {
  margin: 5px 0 15px;
  font-size: 14px;
}

.login-link-btn {
  background: transparent;
  border: 1px solid #4A90E2;
  color: #4A90E2;
  padding: 5px 15px;
  border-radius: 15px;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.3s;
}

.login-link-btn:hover {
  background: #4A90E2;
  color: white;
}

.friend-item {
  display: flex;
  align-items: center;
  padding: 10px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s;
  border: 1px solid transparent;
}

.friend-item:hover {
  background: #f9f9f9;
}

.friend-item.selected {
  background: #eaf2ff;
  border-color: #4a9eff;
}

.friend-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  margin-right: 12px;
  font-size: 14px;
}

.friend-info {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.friend-name {
  font-weight: 500;
  color: #333;
  font-size: 14px;
}

.friend-status {
  font-size: 11px;
  color: #999;
}

.friend-status.online {
  color: #2ecc71;
}

.select-indicator {
  color: #4a9eff;
  font-size: 18px;
}

.modal-footer {
  padding: 15px 20px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.cancel-btn {
  padding: 8px 20px;
  border: none;
  background: #f5f5f5;
  color: #666;
  border-radius: 20px;
  cursor: pointer;
  font-family: '爱点风雅黑';
  transition: all 0.3s;
}

.cancel-btn:hover {
  background: #e0e0e0;
}

.confirm-share-btn {
  padding: 8px 20px;
  border: none;
  background: linear-gradient(135deg, #4A90E2 0%, #357ABD 100%);
  color: white;
  border-radius: 20px;
  cursor: pointer;
  font-family: '爱点风雅黑';
  transition: all 0.3s;
}

.confirm-share-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.confirm-share-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(74, 144, 226, 0.3);
}

/* Dark Theme Support */
.dark-theme .share-modal {
  background: #2d2d2d;
  color: #fff;
}

.dark-theme .modal-header {
  border-bottom-color: #3d3d3d;
}

.dark-theme .modal-header h3 {
  color: #fff;
}

.dark-theme .close-btn {
  color: #aaa;
}

.dark-theme .share-text-box {
  background: #383838;
  border-color: #444;
  color: #ccc;
}

.dark-theme .copy-btn {
  background: #383838;
  border-color: #444;
  color: #ccc;
}

.dark-theme .copy-btn:hover {
  background: #444;
}

.dark-theme .divider {
  color: #666;
}

.dark-theme .divider::before,
.dark-theme .divider::after {
  border-bottom-color: #444;
}

.dark-theme .friend-item:hover {
  background: #383838;
}

.dark-theme .friend-item.selected {
  background: rgba(74, 158, 255, 0.2);
  border-color: #4a9eff;
}

.dark-theme .friend-name {
  color: #fff;
}

.dark-theme .modal-footer {
  border-top-color: #3d3d3d;
}

.dark-theme .cancel-btn {
  background: #383838;
  color: #ccc;
}

.dark-theme .cancel-btn:hover {
  background: #444;
}

/* Animations */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-active .share-modal {
  transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.modal-fade-leave-active .share-modal {
  transition: transform 0.3s ease-in;
}

.modal-fade-enter-from .share-modal {
  transform: scale(0.9) translateY(10px);
}

.modal-fade-leave-to .share-modal {
  transform: scale(0.95) translateY(10px);
}

@media (max-width: 480px) {
  .share-modal {
    width: 100%;
    height: 100vh;
    max-height: none;
    border-radius: 0;
    max-width: none;
  }
}
</style>
